import queryString from 'query-string'
import { cloneDeep, isEmpty } from 'lodash'
import { v4 as uuid } from 'uuid'

import { fetchingAPI, apiService } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { publishInsightEvents } from '@/reducers/insights/insightEvents.actions'
import {
  accessorSectionMap,
  matchUpdateProgress,
  massPlaybookUpdateProgressStatus,
  sections,
} from '@/views/Playbooks/playbook.helpers'
import { convertToSeconds } from '@/views/Playbooks/modules/checklist/ChecklistForm'
import { isLlmChecklistEntry } from '@/views/Playbooks/modules/checklist/checklist.helpers'
import { sanitizeAction } from '@/middleware/sanitizationMiddleware'
import { sanitizeWin } from '@/middleware/sanitizationHelpers'

import { updateDecklistIDs } from './playbook.helpers'
import { setCurrent } from './playbook.redux'
import { createPlaybookVersion, publishPlaybookVersion } from './playbook.actions'
import * as actions from './playbooks.redux'

export const fetchPlaybooksForOrg = async (
  dispatch,
  organizationId,
  getBody,
  isSilent,
  activeOnly
) => {
  const bodyProp = getBody ? ',body' : ''
  const requestedProperties = `name,cid,organization_id,organization_name,config_created,config_agent_app_visibility,language${bodyProp}`
  const params = { requested_properties: requestedProperties }

  if (activeOnly) {
    params.active_only = 'True'
  }

  const requestedPropertiesQueryString = `?${queryString.stringify(params)}`
  const orgFilter = organizationId ? `${organizationId}/` : ''
  const url = `${apiService.core}/api/${orgFilter}configs${requestedPropertiesQueryString}`

  return fetchingAPI(url, 'GET', dispatch, undefined, undefined, { isSilent })
}

export const fetchPlaybooksHelper = async (
  dispatch,
  organizationId,
  getBody,
  isSilent,
  activeOnly
) => {
  const playbooks = await fetchPlaybooksForOrg(
    dispatch,
    organizationId,
    getBody,
    isSilent,
    activeOnly
  )
  dispatch(actions.setPlaybooks(playbooks))
}

export const fetchPlaybooks = (organizationId, getBody, isSilent, activeOnly) => {
  return async (dispatch) => {
    dispatch(actions.setLoading(true))

    try {
      await fetchPlaybooksHelper(dispatch, organizationId, getBody, isSilent, activeOnly)
    } catch (err) {
      dispatch(actions.setMassPlaybookUpdateMatches([]))
      dispatch(actions.setMassPlaybookFetchFailed())
    } finally {
      dispatch(actions.setLoading(false))
    }
  }
}

export const fetchMultipleOrganizationsPlaybooks = (
  organizationIds,
  getBody,
  isSilent,
  activeOnly
) => {
  return async (dispatch) => {
    dispatch(actions.setLoading(true))

    try {
      const playbookPromises = organizationIds.map((organizationId) =>
        fetchPlaybooksForOrg(dispatch, organizationId, getBody, isSilent, activeOnly).catch(
          (error) => {
            console.error(`Failed to fetch playbooks for organization ${organizationId}:`, error)
            return []
          }
        )
      )
      const results = await Promise.all(playbookPromises)
      const allPlaybooks = results.flat()

      dispatch(actions.setPlaybooks(allPlaybooks))
    } finally {
      dispatch(actions.setLoading(false))
    }
  }
}

export const updatePlaybookOrganization = (organizationId, cid) => {
  return async (dispatch) => {
    dispatch(actions.setLoading(true))

    try {
      await fetchingAPI(
        `${apiService.core}/api/configs/${cid}/versions`,
        'PATCH',
        dispatch,
        JSON.stringify({ organization_id: organizationId })
      )
      await fetchPlaybooksHelper(dispatch)
      toast.success('Playbook organization updated')
    } catch (err) {
      toast.error('Failed to update playbook organization', { cause: err })
    } finally {
      dispatch(actions.setLoading(false))
    }
  }
}

export const createPlaybook = (playbook, initialCid, insightEvents, copyNotetakerConfig) => {
  return async (dispatch) => {
    dispatch(actions.setLoading(true))

    try {
      const body = {
        name: playbook.name,
        cid: uuid(),
        began_editing: playbook.began_editing,
        organization_id: playbook.organization_id,
        startingConfigCid: initialCid,
        agent_app_visibility: true,
        ...(copyNotetakerConfig && { copy_notetaker_config: true }),
      }

      const newPlaybook = await fetchingAPI(
        `${apiService.core}/api/configs`,
        'POST',
        dispatch,
        JSON.stringify(body)
      )
      dispatch(publishInsightEvents(insightEvents.events, newPlaybook.id))

      await fetchPlaybooksHelper(dispatch)
      toast.success('New playbook created')
    } catch (err) {
      toast.error('Failed to create playbook', { cause: err })
    } finally {
      dispatch(actions.setLoading(false))
    }
  }
}

export const checkPlaybookForUsers = (
  playbookCid,
  setUsersPreventingDelete,
  setLoading,
  setFetched
) => {
  return async (dispatch) => {
    setLoading(true)

    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/configs/${playbookCid}/users`,
        'GET',
        dispatch
      )

      setUsersPreventingDelete(response)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
      setFetched(true)
    }
  }
}

export const deletePlaybook = (cid) => async (dispatch) => {
  dispatch(actions.setLoading(true))

  try {
    await fetchingAPI(`${apiService.core}/api/configs/${cid}/versions`, 'DELETE', dispatch)
    await fetchPlaybooksHelper(dispatch)
    toast.success('Playbook deleted')
    return true
  } catch (err) {
    toast.error('Failed to delete playbook', { cause: err })
    return false
  } finally {
    dispatch(actions.setLoading(false))
  }
}

export const reassignUserPlaybooks =
  (userIds, cid, setSubmitting, setUsersPreventingDelete) => async (dispatch) => {
    setSubmitting(true)

    try {
      await fetchingAPI(
        `${apiService.core}/api/users/batch`,
        'PATCH',
        dispatch,
        JSON.stringify({ user_ids: userIds, config_cid: cid })
      )
      toast.success('Playbooks reassigned')
      setUsersPreventingDelete([])
    } catch (err) {
      toast.error('Failed to reassign playbook', { cause: err })
    } finally {
      setSubmitting(false)
    }
  }

export const updatePlaybookLanguage = (language, cid) => async (dispatch) => {
  dispatch(actions.setLoading(true))

  try {
    await fetchingAPI(
      `${apiService.core}/api/configs/${cid}/versions`,
      'PATCH',
      dispatch,
      JSON.stringify({ language })
    )
    await fetchPlaybooksHelper(dispatch)
    toast.success('Playbook language updated')
  } catch (err) {
    toast.error('Failed to update playbook language', { cause: err })
  } finally {
    dispatch(actions.setLoading(false))
  }
}

export const updatePlaybookAgentAppVisibility = (cid, isVisibleToAgentApp) => async (dispatch) => {
  dispatch(actions.setLoading(true))

  try {
    await fetchingAPI(
      `${apiService.core}/api/configs/${cid}/versions`,
      'PATCH',
      dispatch,
      JSON.stringify({ agent_app_visibility: isVisibleToAgentApp })
    )
    await fetchPlaybooksHelper(dispatch)
    toast.success('Playbook agent app visibility updated')
  } catch (err) {
    toast.error('Failed to update playbook agent app visibility', { cause: err })
  } finally {
    dispatch(actions.setLoading(false))
  }
}

export const getPlaybookPhraseSuggestions = (
  payload,
  currentEntity,
  isSilent,
  playbookOrganizations = {}
) => {
  return async (dispatch) => {
    dispatch(actions.setLoading(true))
    dispatch(actions.setMassPlaybookUpdateSelection([]))
    dispatch(actions.setMassPlaybookUpdateFetchedEntity(currentEntity))

    try {
      const phaseSuggestions = await fetchingAPI(
        `${apiService.core}/api/configs/playbook_phrase_suggestions`,
        'POST',
        dispatch,
        JSON.stringify(payload),
        undefined,
        { isSilent }
      )
      const phaseSuggestionsExtended = phaseSuggestions?.data?.ndarray.map((item) => {
        const organizationInfo = playbookOrganizations[item.config_id]
        return {
          ...item,
          compoundId: `${item.config_id}_${item.content_id}`,
          progress: matchUpdateProgress.INITIAL,
          configIdOriginal: item.config_id,
          contentIdOriginal: item.content_id,
          organizationId: organizationInfo ? organizationInfo.id : '',
          organizationName: organizationInfo ? organizationInfo.name : '',
        }
      })
      dispatch(actions.setMassPlaybookUpdateMatches(phaseSuggestionsExtended))
    } catch (err) {
      dispatch(actions.setMassPlaybookUpdateMatches([]))
      dispatch(actions.setMassPlaybookFetchFailed())
    } finally {
      dispatch(actions.setLoading(false))
    }
  }
}

export const submitMassPlaybookUpdate = (
  playbooks,
  section,
  matches,
  selection,
  categorySelection,
  config_id,
  content_id,
  categoryId,
  values,
  isExistingItem,
  setShowConfirmPublish
) => {
  return async (dispatch) => {
    let matchesUpdates = cloneDeep(matches)
    const accessor = accessorSectionMap[section]

    try {
      await dispatch(
        actions.setMassPlaybookUpdateProgress(massPlaybookUpdateProgressStatus.IN_PROGRESS)
      )
      setShowConfirmPublish(false)

      // Matches progress update
      matchesUpdates = matchesUpdates.map((item) => ({
        ...item,
        progress: selection.includes(item.compoundId)
          ? matchUpdateProgress.IN_PROGRESS
          : matchUpdateProgress.IGNORE,
      }))
      await dispatch(actions.setMassPlaybookUpdateMatches(matchesUpdates))

      const selectionSelected = selection.map((item) => {
        const [configId, contentId] = item.split('_')
        return {
          config_id: configId,
          content_id: contentId,
        }
      })

      const selectionExtended = [{ config_id, content_id }, ...selectionSelected]

      for (const selected of selectionExtended) {
        const playbook = playbooks.find((p) => p.id.toString() === selected.config_id)
        let newName

        const isCreate = selected.content_id === 'create'
        const selectedContentId = isCreate ? uuid() : selected.content_id

        // Section / Accessor does not exist
        if (isEmpty(playbook.body[accessor])) {
          playbook.body = { ...playbook.body, [accessor]: { entries: {}, order: [] } }
        }

        // Update playbook's selected Checklist
        if (section === sections.CHECKLIST) {
          // Omit `required_before_time_unit` and convert `required_before` to seconds
          const { required_before_time_unit, ...trigger } = values.trigger
          const required_before = convertToSeconds(values)
          const newEntry = {
            ...values,
            trigger: {
              ...trigger,
              required_before,
            },
          }
          sanitizeWin(newEntry)

          if (!newEntry.ai_checklist_prompt?.trim()) {
            delete newEntry.ai_checklist_prompt
          } else {
            newEntry.ai_checklist_prompt = newEntry.ai_checklist_prompt.trim()
          }

          // The config schema only allows a description on LLM checklist items
          if (!isLlmChecklistEntry(newEntry) || !newEntry.description?.trim()) {
            delete newEntry.description
          } else {
            newEntry.description = newEntry.description.trim()
          }

          playbook.body[section].entries[selectedContentId] = newEntry
          newName = newEntry.name

          // In case it's new item add to order as well (before any categorized items)
          // Any bulk add of checklist item will always be uncategorized, we do not support bulk edit/add of the checklist categories
          if (!playbook.body[section].order.includes(selectedContentId)) {
            const categories = playbook.body[section].categories || {}
            const { order } = playbook.body[section]
            const categorizedItemIds = new Set(
              Object.values(categories).flatMap((cat) => cat.order || [])
            )
            const firstCategorizedIndex = order.findIndex((id) => categorizedItemIds.has(id))

            if (firstCategorizedIndex === -1) {
              order.push(selectedContentId)
            } else {
              order.splice(firstCategorizedIndex, 0, selectedContentId)
            }
          }
        }
        // Update playbook's selected Dynamic Prompt / Post Call
        if (section === sections.DYNAMIC_PROMPT || section === sections.POSTCALL) {
          const updateEntry = (catId, contentId) => {
            const sanitizedValues = sanitizeAction({
              accessor,
              payload: {
                entry: values,
                entryId: contentId,
                categoryId: catId,
              },
              sanitizeType: 'entry',
            })
            let newEntry = sanitizedValues.payload.entry
            newEntry = updateDecklistIDs(newEntry)
            return newEntry
          }

          // Add new entry
          if (!isExistingItem) {
            const selectedCategory =
              selected.config_id === config_id
                ? { categoryId, isNew: false }
                : categorySelection[selected.config_id]

            const newEntry = updateEntry(selectedCategory.categoryId, selectedContentId)

            // New category
            if (selectedCategory.isNew) {
              const newCategoryEntity = {
                entries: { [selectedContentId]: newEntry },
                name: selectedCategory.name,
                order: [selectedContentId],
              }
              playbook.body[accessor].entries[selectedCategory.categoryId] = newCategoryEntity
              playbook.body[accessor].order.push(selectedCategory.categoryId)
            }
            // Existing category
            else {
              playbook.body[accessor].entries[selectedCategory.categoryId].entries[
                selectedContentId
              ] = newEntry
              playbook.body[accessor].entries[selectedCategory.categoryId].order.push(
                selectedContentId
              )
            }
          }
          // Update existing entry
          else {
            const categories = playbook.body[accessor].entries ?? {}
            // We are looping through categories until we found one where entry exists (since SLP is giving us back just content_id).
            // When we find and update it we can break out from loop
            for (const catId of Object.keys(categories)) {
              const category = categories[catId]

              if (category.entries?.[selected.content_id]) {
                const newEntry = updateEntry(catId, selected.content_id)

                category.entries[selected.content_id] = newEntry
                newName = newEntry.name

                break
              }
            }
          }
        }

        // Call APIs: Save Draft & Publish
        const newPlaybookId = await dispatch(createPlaybookVersion(playbook))
        if (newPlaybookId) {
          await dispatch(publishPlaybookVersion(newPlaybookId, false))

          if (selected.config_id === config_id && selected.content_id === content_id) {
            // For current item, update new playbook version & set current to false - to disable editing
            await dispatch(actions.setMassPlaybookUpdateNewPlaybookVersion(newPlaybookId))
            await dispatch(setCurrent(false))
          } else {
            // For other items, update progress to DONE
            const compoundId = `${selected.config_id}_${selected.content_id}`

            // Matches progress update
            matchesUpdates = matchesUpdates.map((item) => ({
              ...item,
              config_id: item.config_id === selected.config_id ? newPlaybookId : item.config_id,
              content: item.compoundId === compoundId ? newName : item.content,
              progress: item.compoundId === compoundId ? matchUpdateProgress.DONE : item.progress,
            }))
            await dispatch(actions.setMassPlaybookUpdateMatches(matchesUpdates))
          }
        }
      }

      toast.success('The selected playbooks have been successfully updated')
    } catch (error) {
      toast.error(`Something went wrong while bulk editing the playbooks`, { cause: error })
    } finally {
      dispatch(actions.setMassPlaybookUpdateProgress(massPlaybookUpdateProgressStatus.DONE))
      // Matches progress update - All that stayed IN_PROGRESS -> FAILED
      if (matchesUpdates.some((item) => item.progress === matchUpdateProgress.IN_PROGRESS)) {
        matchesUpdates = matchesUpdates.map((item) => ({
          ...item,
          progress:
            item.progress === matchUpdateProgress.IN_PROGRESS
              ? matchUpdateProgress.FAILED
              : item.progress,
        }))
        await dispatch(actions.setMassPlaybookUpdateMatches(matchesUpdates))
      }
    }
  }
}
export const loadPlaybooks = ({ updateLoading, requestedProperties, organizationId } = {}) => {
  const requestedPropertiesQueryString = requestedProperties
    ? `?${queryString.stringify({ requested_properties: requestedProperties })}`
    : ''
  const url = organizationId
    ? `${apiService.core}/api/${organizationId}/configs${requestedPropertiesQueryString}`
    : `${apiService.core}/api/configs${requestedPropertiesQueryString}`
  return (dispatch) => {
    fetchingAPI(url, 'GET', dispatch)
      .then((playbooks) => {
        dispatch(actions.setPlaybooks(playbooks))
        if (updateLoading) updateLoading(false)
      })
      .catch((err) => {
        console.error('loadPlaybooks failed', err)
        toast.error('Failed to load playbooks', { cause: err })
        if (updateLoading) updateLoading(false)
      })
  }
}

export const fetchVisibilityByCid = (cid) => async (dispatch) => {
  dispatch(actions.setAdditionalLoading('visibility', true))

  try {
    const { visibility } = await fetchingAPI(
      `${apiService.core}/api/configs/${cid}/visibility`,
      'GET',
      dispatch
    )

    dispatch(actions.setVisibilityData(visibility))
  } catch (err) {
    toast.error('Failed to fetch visibility settings', { cause: err })
  } finally {
    dispatch(actions.setAdditionalLoading('visibility', false))
  }
}

export const updatePlaybookVisibility = (cid, visibility) => async (dispatch) => {
  dispatch(actions.setAdditionalLoading('visibilityUpdate', true))

  try {
    await fetchingAPI(
      `${apiService.core}/api/configs/${cid}/visibility`,
      'PUT',
      dispatch,
      JSON.stringify(visibility)
    )
    toast.success('Playbook visibility settings updated')
    return true
  } catch {
    toast.error('Failed to update playbook visibility settings')
    return false
  } finally {
    dispatch(actions.setAdditionalLoading('visibilityUpdate', false))
  }
}
